import { ModalId } from "src/utils/constants";
import { Text, Box, Flex, Button, Group, useMantineTheme } from "@mantine/core";
import {
  GetClientPaymentMethodsManyQuery,
  PaymentMethodType,
  useGetClientPaymentMethodsManyLazyQuery,
} from "src/generated/graphql";
import PaymentMethod from "src/modules/Client/ClientDetail/Account/Agreements/CreateSignedAgreement/PaymentMethod";
import { closeModal, openModal } from "@mantine/modals";
import { useCallback, useEffect, memo } from "react";
import { BsBank, BsCreditCard2Front, BsCreditCardFill } from "react-icons/bs";
import { UnsavedMethod } from "src/api/context/CatalogContext";

export type ISelectedPayment =
  | GetClientPaymentMethodsManyQuery["clientPaymentMethodsMany"][0]
  | null;

type IPaymentSelection = {
  selectedPayment: ISelectedPayment;
  selectedUnsavedMethod?: UnsavedMethod;
  setselectedPayment: (e: ISelectedPayment) => void;
  clientId: string | null | undefined;
  getPrimaryPayment?: boolean;
  permittedMethods?: UnsavedMethod[];
};

function PaymentSelection({
  selectedPayment,
  setselectedPayment,
  clientId,
  getPrimaryPayment = false,
  selectedUnsavedMethod,
  permittedMethods,
}: IPaymentSelection) {
  const theme = useMantineTheme();
  const [getPayments] = useGetClientPaymentMethodsManyLazyQuery({
    onCompleted(data) {
      let paymentPrimary = data.clientPaymentMethodsMany?.find(
        (p) => p.isPrimary
      );
      paymentPrimary && setselectedPayment(paymentPrimary);
    },
  });

  useEffect(() => {
    if (getPrimaryPayment) {
      setselectedPayment(null);
      clientId && getPayments({ variables: { clientId } });
    }
  }, [clientId]);

  const handleModal = useCallback(() => {
    const handleCloseModal = () => {
      closeModal(ModalId.CheckoutPayment);
    };

    const handlePaymentConfirm = (payment: ISelectedPayment) => {
      setselectedPayment(payment);
      handleCloseModal();
    };

    openModal({
      modalId: ModalId.CheckoutPayment,
      closeOnClickOutside: false,
      withCloseButton: false,
      children: (
        <PaymentMethod
          selectedPaymentId={selectedPayment?.id}
          setSelectedPaymentMethod={(e: ISelectedPayment) =>
            handlePaymentConfirm(e)
          }
          handleModalClose={handleCloseModal}
          clientId={clientId || ""}
          permittedMethods={permittedMethods}
        />
      ),
      styles: {
        body: {
          minHeight: "500px",
        },
      },
    });
  }, [clientId, selectedPayment]);

  return (
    <>
      <Flex align={"center"} justify={"space-between"}>
        <Group>
          <Flex
            bg={theme.colors.primary[6]}
            sx={{
              borderRadius: 4,
              width: 40,
              height: 40,
            }}
            align={"center"}
            justify={"center"}
          >
            {!selectedPayment ||
            selectedPayment.type === PaymentMethodType.Card ? (
              <BsCreditCardFill
                size={20}
                color={theme.colors.primaryTextColor[6]}
              />
            ) : (
              <BsBank size={20} color={theme.colors.primaryTextColor[6]} />
            )}
          </Flex>

          {selectedPayment ? (
            <Box sx={{ color: theme.colors.secondaryTextColor[4] }}>
              <Text size={16}>
                {selectedPayment.type === PaymentMethodType.Card
                  ? selectedPayment.merchant
                  : "Bank Account"}
                {" - Ending in "}
                {selectedPayment.mask?.substring(
                  selectedPayment.mask.length - 4
                )}
              </Text>
              {!!selectedPayment.isPrimary && <Text>Primary</Text>}
            </Box>
          ) : selectedUnsavedMethod ? (
            <Box sx={{ color: theme.colors.secondaryTextColor[4] }}>
              <Text size={18}>{selectedUnsavedMethod}</Text>
            </Box>
          ) : (
            <Text color={theme.colors.secondaryTextColor[4]} fw={500}>
              None Selected
            </Text>
          )}
        </Group>
        <Button
          disabled={!clientId}
          onClick={handleModal}
          sx={{
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
        >
          {!selectedPayment ? "Select" : "Change"}
        </Button>
      </Flex>
    </>
  );
}

export default memo(PaymentSelection);
